import React, { useEffect, useState } from 'react';
import { FlameIcon, UsersIcon } from 'lucide-react';
import { RollingNumber } from './RollingNumber';
// Scarcity block for /demo: "only 100 lucky members at this price — when we hit
// 100 the price goes back to $1,997". Shows a live members-claimed meter that
// climbs toward 100 (capped so the offer stays buyable) plus a countdown timer.
// The start point is persisted per visitor via a cookie so the numbers stay
// consistent across reloads within the window (mirrors useSpotCountdown).
const TOTAL_SPOTS = 100;
const COOKIE_NAME = 'scarcity_100_start';
const WINDOW_MS = 24 * 60 * 60 * 1000; // countdown window: 24h from first visit
// Members already claimed when the visitor first lands, then it climbs.
// Exported so a parent (e.g. /demo) can seed a shared, controlled count from
// the same baseline the standalone meter uses.
export const START_CLAIMED = 71;
// Never fully sell out — hold at 97 so there are always a few spots left.
export const MAX_CLAIMED = 97;
function setCookie(name: string, value: string, ms: number) {
  const date = new Date();
  date.setTime(date.getTime() + ms);
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
}
function getCookie(name: string): string | null {
  const nameEQ = name + '=';
  const parts = document.cookie.split(';');
  for (let c of parts) {
    while (c.charAt(0) === ' ') c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
  }
  return null;
}
type ScarcityCountdownProps = {
  /**
   * When provided, the members-claimed count is CONTROLLED by the parent (e.g.
   * /demo drives it up in lockstep with each sales pop-up). When omitted, the
   * component falls back to its own self-contained cookie/time-based climb.
   */
  claimed?: number;
};
export function ScarcityCountdown({
  claimed: claimedProp
}: ScarcityCountdownProps = {}) {
  const isControlled = typeof claimedProp === 'number';
  const [internalClaimed, setInternalClaimed] = useState(START_CLAIMED);
  useEffect(() => {
    // Skip the self-contained timer when the parent controls the count.
    if (isControlled) return;
    let start = getCookie(COOKIE_NAME);
    const now = Date.now();
    if (start) {
      const age = now - parseInt(start, 10);
      if (!Number.isFinite(age) || age >= WINDOW_MS) start = null;
    }
    if (!start) {
      start = now.toString();
      setCookie(COOKIE_NAME, start, WINDOW_MS);
    }
    const startTs = parseInt(start, 10);
    const tick = () => {
      const elapsed = Date.now() - startTs;
      // Members climb: ~1 member every ~3.5 min from START_CLAIMED, capped at
      // MAX_CLAIMED so there are always spots left to buy.
      const gained = Math.floor(elapsed / (3.5 * 60 * 1000));
      setInternalClaimed(Math.min(MAX_CLAIMED, START_CLAIMED + gained));
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [isControlled]);
  // Controlled value is clamped to the same buyable ceiling so the meter never
  // "sells out" even if the parent keeps counting.
  const claimed = isControlled ? Math.min(MAX_CLAIMED, Math.max(START_CLAIMED, claimedProp as number)) : internalClaimed;
  const spotsLeft = Math.max(0, TOTAL_SPOTS - claimed);
  const pct = Math.min(100, Math.round(claimed / TOTAL_SPOTS * 100));
  return <section className="rounded-2xl border-2 border-[#FD0200] bg-white shadow-[0_10px_30px_rgba(253,2,0,0.12)] px-4 py-5 sm:px-6 sm:py-6 text-center" role="region" aria-label="Oferta por tiempo y cupos limitados" data-id="element-195" data-mp="2heb2kekota7x">
      {/* Headline */}
      <div className="inline-flex items-center gap-2 rounded-full bg-[#FEF2F2] text-[#FD0200] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[1.5px] mb-3" data-id="element-196" data-mp="23gmdsvni1ixl">
        <FlameIcon className="w-3.5 h-3.5" data-id="element-197" data-mp="1b03dd9umfhjh" />
        Solo 100 miembros afortunados
      </div>
      <h3 className="font-['Anton'] uppercase tracking-[0.5px] leading-[1.1] text-[clamp(18px,3.8vw,26px)] text-[#050505] mb-1.5" data-id="element-198" data-mp="1axqm262z3utr">
        Cuando lleguemos a <span className="text-[#FD0200]" data-id="element-199" data-mp="9an04tlhjpg7">100 miembros</span>{' '}
        el precio vuelve a <span className="text-[#FD0200]" data-id="element-200" data-mp="dafa9aqj23no">$1,997</span>
      </h3>
      <p className="text-[13px] sm:text-[14px] text-[#65676b] leading-snug mb-4 max-w-[460px] mx-auto" data-id="element-201" data-mp="d8uh9pjv2srx">
        Asegura tu lugar por <b className="text-[#050505]" data-id="element-202" data-mp="n1jpmbk47qj1">SOLO $27</b> antes de
        que se acaben los cupos.
      </p>

      {/* Members progress */}
      <div className="max-w-[460px] mx-auto mb-4" data-id="element-203" data-mp="rzl5knm8v9qa">
        <div className="flex items-center justify-between text-[12px] font-semibold text-[#050505] mb-1.5" data-id="element-204" data-mp="wyxqv3xgw8qr">
          <span className="inline-flex items-center gap-1.5" data-id="element-205" data-mp="1109ljnv77k5t">
            <UsersIcon className="w-3.5 h-3.5 text-[#FD0200]" data-id="element-206" data-mp="10yonho9odj37" />
            <RollingNumber value={claimed} className="font-['Anton'] text-[15px] text-[#FD0200]" data-id="element-207" data-mp="15ykuzxhkzsni" />
            <span className="text-[#65676b] font-medium" data-id="element-208" data-mp="32tfvjns4s47r">/ 100 reclamados</span>
          </span>
          <span className="text-[#FD0200] font-extrabold" data-id="element-209" data-mp="37safit1u3ytg">
            ¡Solo quedan {spotsLeft}!
          </span>
        </div>
        <div className="h-3 w-full rounded-full bg-[#f0f2f5] overflow-hidden" role="progressbar" aria-valuenow={claimed} aria-valuemin={0} aria-valuemax={TOTAL_SPOTS} data-id="element-210" data-mp="309146u9rpq1r">
          <div className="h-full rounded-full bg-gradient-to-r from-[#FD0200] to-[#AA0E13] transition-[width] duration-700 ease-out" style={{
          width: `${pct}%`
        }} data-id="element-211" data-mp="2qiybkv6k6e3y" />
        </div>
      </div>
    </section>;
}